import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Heart, Users, Calendar, Star } from "lucide-react";
import { HeroCarousel } from "./HeroCarousel";
import reunionPosterBg from "@/assets/reunion-poster-bg.jpeg";

export const Hero = () => {
  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Animated Background Image with Ken Burns effect */}
      <motion.div
        className="absolute inset-0 w-full h-full"
        animate={{
          scale: [1, 1.15, 1],
          x: ["0%", "3%", "-3%", "0%"],
          y: ["0%", "-2%", "2%", "0%"],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <img
          src={reunionPosterBg}
          alt="A Sweet Reunion - Documentary Background"
          className="w-full h-full object-cover"
        />
      </motion.div>
      {/* Gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40" />
      {/* Cinematic vignette effect */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.8) 100%)"
        }}
      />
      {/* Film grain texture overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.15] mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />
      
      {/* Hero Content */}
      <div className="relative z-10 pt-20 pb-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Campaign Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white bg-black/30 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10"
            >
              <div className="flex items-center gap-2 mb-6">
                <Badge className="bg-story-hope text-black font-semibold px-3 py-1">
                  <Star className="w-4 h-4 mr-1" />
                  FEATURED PROJECT
                </Badge>
                <Badge variant="outline" className="border-white/30 text-white">
                  <Calendar className="w-4 h-4 mr-1" />
                  30 DAYS LEFT
                </Badge>
              </div>
              
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl mb-6 leading-tight">
                A Sweet Reunion
                <span className="block text-story-hope">26 Years Apart</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                Help reunite a refugee boy and the woman who saved his life. After 26 years apart, Tachen and Bettina found each other again through Facebook. Now, let's bring them together for the reunion they've both dreamed of.
              </p>
              
              {/* Stats */}
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-story-hope" />
                  <span className="text-lg font-semibold">247 backers</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-story-peace" />
                  <span className="text-lg font-semibold">1,234 supporters</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-story-hope hover:bg-story-hope/90 text-black font-bold text-lg px-8 py-4 h-auto shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  onClick={() => document.getElementById('fund-project')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Back This Project
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 font-semibold text-lg px-8 py-4 h-auto backdrop-blur-sm"
                  onClick={() => document.getElementById('documentary')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Watch Preview
                </Button>
              </div>
            </motion.div>
            
            {/* Right Column - Media Carousel */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <HeroCarousel />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};